import React, { useState, useEffect } from 'react';
import { useAccount, useDisconnect, useWalletClient, usePublicClient } from 'wagmi';
import { ethers } from 'ethers';
import alertify from 'alertifyjs';
import './App.css';
import 'alertifyjs/build/css/alertify.css';
import 'alertifyjs/build/css/themes/default.css';
import CountdownTimer from './CountdownTimer';

const App = () => {
  const { address, isConnected } = useAccount();
  const { disconnect } = useDisconnect();
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();

  const [amount, setAmount] = useState('');
  const [clcEquivalent, setClcEquivalent] = useState('0 CLC');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [txHash, setTxHash] = useState(null);
  const [txStatus, setTxStatus] = useState(null);

  const [presaleStartDate, setPresaleStartDate] = useState(new Date('2024-11-03'));
  const [raisedAmount, setRaisedAmount] = useState(0);
  const [currentPhase, setCurrentPhase] = useState('Phase 1');

  useEffect(() => {
    if (!window.ReactWidgetSettings) {
      console.error('ReactWidgetSettings is not defined');
    } else {
      const { startDate, currentPhase, raisedAmount } = window.ReactWidgetSettings;

      if (startDate) setPresaleStartDate(new Date(startDate));
      if (currentPhase) setCurrentPhase(currentPhase);
      if (raisedAmount) setRaisedAmount(parseFloat(raisedAmount));
    }
  }, []);

  const phaseGoal = 1000000; // $1 million per phase
  const phaseDuration = 30; // Each phase lasts 30 days

  const currentDate = new Date();
  const timeElapsed = Math.floor((currentDate - presaleStartDate) / (1000 * 60 * 60 * 24));

  let phaseEndDate;
  let clcPriceInBNB;

  if (timeElapsed < phaseDuration) {
    phaseEndDate = new Date(presaleStartDate.getTime() + phaseDuration * 24 * 60 * 60 * 1000);
    clcPriceInBNB = 0.00000089; // Phase 1 price
  } else if (timeElapsed < 2 * phaseDuration) {
    phaseEndDate = new Date(presaleStartDate.getTime() + 2 * phaseDuration * 24 * 60 * 60 * 1000);
    clcPriceInBNB = 0.0000011; // Phase 2 price
  } else if (timeElapsed < 3 * phaseDuration) {
    phaseEndDate = new Date(presaleStartDate.getTime() + 3 * phaseDuration * 24 * 60 * 60 * 1000);
    clcPriceInBNB = 0.0000015; // Phase 3 price
  } else {
    clcPriceInBNB = 0; // No price after presale
  }

  const daysRemaining = phaseEndDate ? Math.max(0, Math.ceil((phaseEndDate - currentDate) / (1000 * 60 * 60 * 24))) : 0;

  useEffect(() => {
    const buyButton = document.getElementById('buy-tokens');
    if (isConnected) {
      if (buyButton) {
        buyButton.style.display = 'inline-block';
        buyButton.disabled = parseFloat(amount) <= 0;
      }
    } else {
      if (buyButton) buyButton.style.display = 'none';
    }
  }, [isConnected, amount]);

  const updateTokenEquivalent = async (event) => {
    const input = event.target.value;
    setAmount(input);

    if (!input || parseFloat(input) <= 0) {
      setClcEquivalent('0 CLC');
      return;
    }

    const usdAmount = parseFloat(input);
    const response = await fetch('https://api.binance.com/api/v3/ticker/price?symbol=BNBUSDT');
    const data = await response.json();
    const bnbPriceInUSD = parseFloat(data.price);

    const bnbAmount = usdAmount / bnbPriceInUSD;
    const clcEquivalent = (bnbAmount / clcPriceInBNB).toFixed(2);
    setClcEquivalent(`${clcEquivalent} CLC`);
  };

  const handleInputKeyDown = (event) => {
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
    const hexPattern = /^[0-9.]$/;

    if (!allowedKeys.includes(event.key) && !hexPattern.test(event.key)) {
      event.preventDefault();
    }
  };

  const buyTokens = async () => {
    try {
      setLoading(true);
      setError('');

      const usdAmount = parseFloat(amount);
      const response = await fetch('https://api.binance.com/api/v3/ticker/price?symbol=BNBUSDT');
      const data = await response.json();
      const bnbPriceInUSD = parseFloat(data.price);
      const bnbAmount = (usdAmount / bnbPriceInUSD).toFixed(6);

      const amountInWei = ethers.parseEther(bnbAmount);

      const transactionParameters = {
        to: '0x5482146b7f1eE9028317331f7696d4F9C18B5b72',
        from: address,
        value: amountInWei,
        gasLimit: ethers.toBeHex(21000),
      };

      alertify.confirm(
        'Confirmation',
        `You are about to buy ${clcEquivalent} tokens for ${bnbAmount} BNB. Do you want to proceed?`,
        async () => {
          try {
            const txResponse = await walletClient.sendTransaction(transactionParameters);
            setTxHash(txResponse);
            setRaisedAmount((prevAmount) => {
              const newRaisedAmount = prevAmount + usdAmount;
              localStorage.setItem('raisedAmount', newRaisedAmount.toString());
              return newRaisedAmount;
            });
            alertify.success('Transaction sent! Waiting for confirmation...');
          } catch (error) {
            alertify.error('The transaction could not be completed. Please try again.');
            setLoading(false);
          }
        },
        () => {
          alertify.error('Transaction canceled by the user.');
          setLoading(false);
        }
      ).set({
        title: 'Transaction Confirmation',
        labels: { ok: 'Buy', cancel: 'Cancel' },
      });
    } catch (error) {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  useEffect(() => {
    const checkTransactionStatus = async () => {
      if (txHash) {
        try {
          const receipt = await publicClient.waitForTransactionReceipt({ hash: txHash });
          if (receipt) {
            setTxStatus(receipt.status === 'success' ? 'Success' : 'Failed');
            alertify[receipt.status === 'success' ? 'success' : 'error'](`Transaction ${receipt.status}!`);
          } else {
            setTxStatus('Pending');
            alertify.message('Transaction is pending...');
          }
        } catch (error) {
          setTxStatus('Error');
          alertify.error('Error fetching transaction receipt.');
        }
        setLoading(false);
      }
    };

    checkTransactionStatus();
  }, [txHash, publicClient]);

  const progressPercentage = (raisedAmount / phaseGoal) * 100;

  return (
    <div id="div-container">
      <div id="payment-container">
        <label htmlFor="amount">Amount in USD you pay:</label>
        <input
          type="text"
          id="amount"
          name="amount"
          value={amount}
          onChange={updateTokenEquivalent}
          onKeyDown={handleInputKeyDown}
          placeholder="Enter amount in USD"
        />
        <div id="token-equivalent">{clcEquivalent}</div>

        <div className="phase-info">
          <span>Current Phase: <strong>{currentPhase}</strong></span>
        </div>

        <div className="progress-bar-container">
          <div className="progress-bar" style={{ width: `${progressPercentage}%` }}>
            <span className="progress-percentage">{progressPercentage.toFixed(1)}%</span>
          </div>
        </div>

        <div className="progress-amount-info">
          <span>${raisedAmount.toLocaleString()} / $1,000,000</span>
        </div>

        <div className="price-info">
          <span>Current Price: {clcPriceInBNB.toFixed(8)} BNB</span>
        </div>

        <div className="price-info">
          <h2>Price increases in</h2>
          <CountdownTimer targetDate={phaseEndDate} />
        </div>

        {isConnected ? (
          <>
            <button id="disconnect-wallet" onClick={disconnect}>Disconnect Wallet</button>
            <button id="buy-tokens" onClick={buyTokens} disabled={parseFloat(amount) <= 0}>
              {loading ? 'Processing...' : 'Buy ClusterC'}
            </button>
          </>
        ) : (
          <div id="center-container">
            <w3m-connect-button></w3m-connect-button>
          </div>
        )}
        {error && <div style={{ color: 'red' }}>{error}</div>}
        {loading && <div className="loader"></div>}
      </div>
    </div>
  );
};

export default App;
